/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// formik components
import { Formik, Form } from "formik";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

// NewUser page components
import UserInfo from "views/users/account/components/BasicInfo/UserInfo";

// NewUser layout schemas for form and form feilds
import validations from "views/users/account/components/BasicInfo/schemas/validations";
import form from "views/users/account/components/BasicInfo/schemas/form";
import initialValues from "views/users/account/components/BasicInfo/schemas/initialValues";

// sweetalert2 components
import Swal from "sweetalert2";

import { putData } from "Utils/server";
import { getAuthorizationHeader, getUserData, setUserData } from "Utils/helpers";

function getSteps() {
  return ["User Info"];
}

function BasicInfo({ signalCloseModal, informParent }) {
  const [activeStep, setActiveStep] = useState(1);
  const steps = getSteps();
  const { formId, formField } = form;
  const currentValidation = validations[activeStep];
  const isLastStep = activeStep === steps.length - 1;
  const [formValues, setFormValues] = useState(initialValues);

  const userData = getUserData();

  function initData(data) {
    if (!data) data = userData;
    initialValues.firstName = data.first_name ? data.first_name : "";
    initialValues.lastName = data.last_name ? data.last_name : "";
    initialValues.username = data.username ? data.username : "";
    initialValues.email = data.email ? data.email : "";
    setFormValues(initialValues);
    setActiveStep(0);
  }

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  
  const submitForm = async (values, actions) => {
    await sleep(1000);

    let objSent = { ...values };
    objSent.role_id = userData.roles[0].id;
    objSent.status = userData.status;

    const postProfileEndpoint = `/api/v1/users/${userData.id}/profile`;

    const setProfileResponse = await putData(postProfileEndpoint, objSent, {
      Authorization: getAuthorizationHeader(),
    });

    if (setProfileResponse) {
      Swal.fire("Request Sent!", `Your Account is Succesfully Updated.`, "success");
      signalCloseModal(false, null);

      setUserData({ key: "username", value: objSent.username });
      setUserData({ key: "first_name", value: objSent.firstName });
      setUserData({ key: "last_name", value: objSent.lastName });
      setUserData({ key: "email", value: objSent.email });

      actions.setSubmitting(false);
    } else {
      Swal.fire(
        "Error!",
        `Something goes Wrong while Updating your Account. Please try again later.`,
        "error"
      );
    }

    if (setProfileResponse) {
      signalCloseModal(false, null);

      setUserData({ key: "username", value: objSent.username });
      setUserData({ key: "first_name", value: objSent.firstName });
      setUserData({ key: "last_name", value: objSent.lastName });
      setUserData({ key: "email", value: objSent.email });

      actions.setSubmitting(false);
    }
  };

  const handleSubmit = (values, actions) => {
    submitForm(values, actions);
  };

  const handleCancelCreation = (values, actions) => {
    //console.log("reseting...");
    informParent(false);
  };

  useEffect(() => {
    if (initialValues) {
      initData();
    }
  }, [initialValues]);

  return (
    <Card id="basic-info" sx={{ overflow: "visible" }}>
      <SoftBox p={3}>
        <SoftTypography variant="h5">Basic Account Info</SoftTypography>
      </SoftBox>
      <SoftBox pb={3} px={3}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item sx={{ height: "100%", width: "100%" }}>
            <Formik
              initialValues={formValues}
              validationSchema={currentValidation}
              onSubmit={handleSubmit}
              onReset={handleCancelCreation}
            >
              {({ values, errors, touched, isSubmitting }) => (
                <Form id={formId} autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        <UserInfo
                          formData={{
                            values,
                            touched,
                            formField,
                            errors,
                          }}
                          informParent={informParent}
                        />
                      </SoftBox>
                      <SoftBox>
                        <Grid container justifyContent="right">
                          <Grid item>
                            <SoftBox ml="auto" sx={{ marginRight: "10px" }}>
                              <SoftButton
                                variant="outlined"
                                color="secondary"
                                size="small"
                                type="reset"
                              >
                                Cancel
                              </SoftButton>
                            </SoftBox>
                          </Grid>
                          <Grid item>
                            <SoftBox ml="auto">
                              <SoftButton
                                type="submit"
                                variant="gradient"
                                color="dark"
                                size="small"
                              >
                                update
                              </SoftButton>
                            </SoftBox>
                          </Grid>
                        </Grid>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </Card>
  );
}

BasicInfo.propTypes = {
  signalCloseModal: PropTypes.func,
  informParent: PropTypes.func,
};
export default BasicInfo;
